import { atom } from "jotai"
import { projectAtom, updateCurrentProject } from "@/store/project/projects-state"
import {
	CatalogManager,
	ImageInfoRecord,
	PhotoRecord,
	StyleInfoRecord,
	StyleReferenceRecord,
} from "@/lib/catalog"
import { store } from "../store"
import { pushDialog, replaceDialog, resetDialogs } from "../dialogs-state"
import { Sentry } from "@/telemetry/sentry"
// eslint-disable-next-line no-restricted-imports -- Phase 1: store-layer i18n
import i18n from "@/i18n"
import { ConfirmDialog, ImageStyle } from "@/components/Dialogs/ConfirmDialog"
import { StylePreviewDialog } from "@/components/Dialogs/StylePreviewDialog"
import { StyleNamingDialog } from "@/components/Dialogs/StyleNamingDialog"
import { DialogProps } from "@/components/ui/Dialog"
import styleBubblesUrl from "@assets/style_bubbles.png?url"
import { addStyleBiases, getScaledDeltas } from "../processing/image-processing"
import { Adjustments } from "@/core/types"
import { StyleResponseDTO, StyleVisibility } from "@/api/dto/styles"
import { StyleReferenceResponseDTO } from "@/api/dto/style-references"
import { atomWithInfiniteQuery, atomWithQuery } from "jotai-tanstack-query"
import { InfiniteData } from "@tanstack/query-core"
import { PNApiOperations } from "@/api/operations"
import { ListingResponseDTO } from "@/api/dto/shared"
import { publishCurrentStyleAsNewStyle, updateCurrentStyleInTheCloud } from "./publish-style"
import { analytics } from "@/telemetry/amplitude"
import { AddEditsToStyleDialog } from "@/components/Dialogs/AddEditsToStyleDialog"
import { StyleService } from "./style-service"
import { photosAtom, updatePhotos } from "../photo/photos-state"
import { isDeepEqual } from "@/utils/object"
import { selectedCategoryIdAtom } from "./polarr-styles"
import { showInternalStylesAtom } from "../settings-state"
import { userQueryAtom } from "@/api/users-api"
import { isStaffPlusRole } from "@/utils/user-role"
import { getFinalizeAIStyleProperties } from "./style-event-utils"
import { getDuplicateStyleNameErrorMessage, isDuplicateStyleNameError } from "./style-service"
import { popActiveLoadingDialogs } from "@/store/loading-dialog-utils"
import { isNetworkConnectivityError, showActiveNetworkErrorToast } from "@/api/network-behavior"
import { AxiosError } from "axios"

export interface PreviewableStyle {
	name: string
	referenceImages: Record<string, ImageInfoRecord>
	presets: Record<string, Partial<Adjustments>>
	prioritizeImageIds?: string[]
	globalBiases?: Partial<Adjustments>
	styleCreationIncludePreset?: boolean | null
}

export interface StyleDraft extends PreviewableStyle {
	imagesData?: Record<string, Blob>
	visibility?: StyleVisibility
}

export class RemoteStyle implements PreviewableStyle {
	referenceImages: Record<string, ImageInfoRecord>
	prioritizeImageIds?: string[]
	globalBiases?: Partial<Adjustments>
	constructor(
		public response: StyleResponseDTO,
		public references: StyleReferenceResponseDTO[],
	) {
		this.referenceImages = references.reduce((acc, ref) => {
			if (ref.imageInfo) {
				acc[ref.imageInfo.imageId] = ref.imageInfo
			}
			return acc
		}, {})
	}

	get name(): string {
		return this.response.name
	}

	get presets(): Record<string, Partial<Adjustments>> {
		return this.response.presets
	}
}

export type Style = StyleInfoRecord | PreviewableStyle
export type ProjectSetupStyle = StyleInfoRecord | RemoteStyle

export interface SelectStyleOptions {
	style: ProjectSetupStyle | "new" | "manual"
	needsSetup: boolean
	isExpressSetup?: boolean
	publishedReferences?: StyleReferenceRecord[]
}

export const isRemoteStyle = (style: Style): style is RemoteStyle => {
	return "response" in style
}

export const isLocalStyle = (style: Style): style is StyleInfoRecord => {
	return "styleId" in style && !("response" in style)
}

export const currentStyleAtom = atom<StyleInfoRecord | null>(null)

export const styleReferencesAtom = atom<StyleReferenceRecord[]>([])

export const isProjectImplicitReference = (ref: StyleReferenceRecord): boolean => {
	const photos = store.get(photosAtom)
	const photoIds = Object.keys(photos)
	return ref.isImplicit && photoIds.includes(ref.imageInfo.imageId)
}

export const filteredStyleReferencesAtom = atom((get) => {
	const references = get(styleReferencesAtom)
	return references.filter((ref) => {
		return !isProjectImplicitReference(ref)
	})
})

export const referenceImagesAtomFamily = (filterCurrentProjectImplicit: boolean = true) =>
	atom((get) => {
		const references = filterCurrentProjectImplicit
			? get(filteredStyleReferencesAtom)
			: get(styleReferencesAtom)
		const images: Record<string, ImageInfoRecord> = {}
		for (const ref of references) {
			if (ref.imageInfo) {
				images[ref.imageInfo.imageId] = ref.imageInfo
			}
		}
		return images
	})

export type ProjectStyleTypes =
	| "My AI Style"
	| "Polarr AI Style"
	| "Pixieset AI Style"
	| "Pixieset Gallery"
	| "Images"
	| "Editing"
	| "Manual"
	| "Reapply"

const fetchPolarrStylesAtom = atomWithQuery<ListingResponseDTO<StyleResponseDTO>>((get) => {
	const selectedCategoryId = get(selectedCategoryIdAtom)

	return {
		queryKey: ["polarr_styles", selectedCategoryId],
		queryFn: async () => {
			if (!selectedCategoryId) {
				return { data: [], hasMore: false }
			}
			// Fetch styles for specific category
			return PNApiOperations.getCategoryStyles(selectedCategoryId)
		},
		enabled: !!selectedCategoryId,
	}
})

export const displayStyleIdAtom = atom<string | null>(null)
export const fetchStyleReferencesAtom = atomWithQuery<StyleReferenceRecord[]>((get) => ({
	queryKey: ["style_references", get(displayStyleIdAtom)],
	queryFn: async ({ queryKey: [, id] }) => {
		if (id) {
			const result = await PNApiOperations.getStyleReferences(id as string)
			const records = result.data.map((dto) => StyleService.responseToReference(dto))
			return records
		} else {
			return []
		}
	},
}))

export const polarrStylesAtom = atom<{ data: StyleResponseDTO[]; isInitialLoading: boolean }>(
	(get) => {
		const { data, isInitialLoading, isError } = get(fetchPolarrStylesAtom)

		if (data && !isError) {
			for (const style of data.data) {
				if (typeof style.createdAt === "string") {
					style.createdAt = new Date(style.createdAt)
				}
				if (typeof style.updatedAt === "string") {
					style.updatedAt = new Date(style.updatedAt)
				}
				// Categories are now set by the backend
			}
			return { data: data.data, isInitialLoading }
		}

		return {
			data: [],
			isInitialLoading,
		}
	},
)

const USER_STYLES_PAGE_SIZE = 50

const fetchUserStylesAtom = atomWithInfiniteQuery<
	ListingResponseDTO<StyleResponseDTO>,
	Error,
	InfiniteData<ListingResponseDTO<StyleResponseDTO>, string | undefined>,
	string[],
	string | undefined
>(() => ({
	queryKey: ["user_styles"],
	queryFn: ({ pageParam }) =>
		PNApiOperations.getStyles({
			size: USER_STYLES_PAGE_SIZE,
			anchor: typeof pageParam === "string" ? pageParam : undefined,
		}),
	getNextPageParam: (lastPage: ListingResponseDTO<StyleResponseDTO>) =>
		lastPage.hasMore ? lastPage.anchor : undefined,
	initialPageParam: undefined,
}))

export const userStylesAtom = atom<{
	data: StyleResponseDTO[]
	isInitialLoading: boolean
	isFetchingNextPage: boolean
	hasNextPage: boolean
	fetchNextPage: () => Promise<unknown>
}>((get) => {
	const { data, isInitialLoading, isError, isFetchingNextPage, hasNextPage, fetchNextPage } =
		get(fetchUserStylesAtom)
	if (data && !isError) {
		return {
			data: data.pages.flatMap((page) => page.data),
			isInitialLoading,
			isFetchingNextPage,
			hasNextPage: Boolean(hasNextPage),
			fetchNextPage,
		}
	}

	return {
		data: [],
		isInitialLoading,
		isFetchingNextPage,
		hasNextPage: Boolean(hasNextPage),
		fetchNextPage,
	}
})

const fetchInternalStylesAtom = atomWithQuery<ListingResponseDTO<StyleResponseDTO>>((get) => {
	const showInternalStyles = get(showInternalStylesAtom)

	return {
		queryKey: ["internal_styles", showInternalStyles],
		queryFn: PNApiOperations.getInternalStyles,
		enabled: showInternalStyles,
	}
})

export const internalStylesAtom = atom<{ data: StyleResponseDTO[]; isInitialLoading: boolean }>(
	(get) => {
		const showInternalStyles = get(showInternalStylesAtom)
		if (!showInternalStyles) {
			return {
				data: [],
				isInitialLoading: false,
			}
		}

		const { data, isInitialLoading, isError } = get(fetchInternalStylesAtom)
		if (data && !isError) {
			return { data: data.data, isInitialLoading }
		}

		return {
			data: [],
			isInitialLoading,
		}
	},
)

export const tuneStyleWithPreviewAdjustments = <TStyle extends PreviewableStyle>(
	style: TStyle,
	photoIds: string[],
	deltas: Record<string, number>,
) => {
	const scaledDeltas = getScaledDeltas(deltas)
	const updatedReferenceImages: Record<string, ImageInfoRecord> = {}

	for (const [imageId, imageInfo] of Object.entries(style.referenceImages)) {
		let styleBiases: Partial<Adjustments> = scaledDeltas
		if (imageInfo.styleBiases) {
			styleBiases = addStyleBiases(imageInfo.styleBiases, scaledDeltas)
		}
		updatedReferenceImages[imageId] = {
			...imageInfo,
			styleBiases,
		}
	}
	// Save prioritizeImageIds to style so that these photos are processed first when applying style to avoid causing inconsistency
	style.referenceImages = updatedReferenceImages
	style.prioritizeImageIds = photoIds
	style.globalBiases = deltas

	// Return updated style
	return style
}

export const saveAsCurrentStyle = async (
	style: StyleInfoRecord,
	references?: StyleReferenceRecord[],
	skipReprocessing: boolean = false,
) => {
	const currentStyle = store.get(currentStyleAtom)
	if (isDeepEqual(currentStyle, style)) {
		return
	}
	store.set(currentStyleAtom, style)
	await CatalogManager.storeStyleInfo(style)

	if (references && references.length > 0) {
		await CatalogManager.replaceStyleReferences(references)
		store.set(styleReferencesAtom, references)
	}

	if (!currentStyle || currentStyle.styleId !== style.styleId) {
		await updateCurrentProject({ styleId: style.styleId })
		if (skipReprocessing && currentStyle) {
			await overwriteStyleInPhotos(currentStyle.styleId, style)
		}
	}
}

export const overwriteStyleInPhotos = async (oldStyleId: string, newStyle: StyleInfoRecord) => {
	const allPhotos = Object.values(store.get(photosAtom))
	const photosWithOldStyle = allPhotos.filter((photo) => photo.styleId === oldStyleId)
	await updatePhotos((photos) => {
		for (const photo of photosWithOldStyle) {
			photos[photo.id] = { styleId: newStyle.styleId, styleVersion: newStyle.version }
		}
	})
}

export const currentStyleCreationIdAtom = atom<string | null>(null)

const logApplyStyle = (projectStyleType: ProjectStyleTypes, style: Style) => {
	const project = store.get(projectAtom)
	const isExpressSetup = project?.isExpressSetup
	const styleCreationIncludePreset =
		"styleCreationIncludePreset" in style ? style.styleCreationIncludePreset : undefined
	analytics.trackEvent({
		label: "finalize_ai_style",
		customProperties: getFinalizeAIStyleProperties({
			projectStyleType,
			style,
			projectId: project?.id,
			isExpressSetup,
			styleCreationIncludePreset,
		}),
		type: "event",
	})
}

export const showStylePreviewAndNamingDialog = (
	dialogProps: DialogProps,
	styleDraft: StyleDraft,
	projectStyleType: ProjectStyleTypes,
	previewPhotos: PhotoRecord[],
	onStyleFinalized: (style: StyleDraft) => void | Promise<void>,
	errorMessage?: string,
) => {
	if (previewPhotos.length === 0) {
		showStyleNamingDialog(
			dialogProps,
			styleDraft,
			projectStyleType,
			onStyleFinalized,
			errorMessage,
		)
		return
	}

	replaceDialog(StylePreviewDialog, {
		...dialogProps,
		trackingViewName: "style_preview_dialog",
		style: styleDraft,
		samplePhotos: previewPhotos,
		actionTitle: i18n.t("store:styleState.nameStyleButton"),
		onStyleFinalized: (finalizedStyle) => {
			showStyleNamingDialog(
				dialogProps,
				finalizedStyle,
				projectStyleType,
				onStyleFinalized,
				errorMessage,
			)
		},
	})
}

export const showStyleNamingDialog = <TStyle extends PreviewableStyle>(
	dialogProps: DialogProps,
	style: TStyle,
	projectStyleType: ProjectStyleTypes,
	onStyleFinalized: (style: TStyle) => void | Promise<void>,
	errorMessage?: string,
) => {
	replaceDialog(StyleNamingDialog, {
		...dialogProps,
		canGoBack: false,
		initialName: style.name,
		trackingViewName: "style_created_dialog",
		title: i18n.t("store:styleState.aiStyleCreatedTitle"),
		subTitle: i18n.t("store:styleState.nameYourAiStyle"),
		actionTitle: i18n.t("store:styleState.saveStyleButton"),
		errorMessage,
		onCompleteWithError: async (result: { name: string }) => {
			style.name = result.name
			try {
				await onStyleFinalized(style)
			} catch (error) {
				if (isDuplicateStyleNameError(error)) {
					return getDuplicateStyleNameErrorMessage(result.name)
				}
				throw error
			}
			logApplyStyle(projectStyleType, style)
		},
	})
}

export const showStylePreviewDialog = <TStyle extends PreviewableStyle>(
	dialogProps: DialogProps,
	style: TStyle,
	projectStyleType: ProjectStyleTypes,
	previewPhotos: PhotoRecord[],
	onStyleFinalized: (style: TStyle) => void | Promise<void>,
) => {
	if (previewPhotos.length === 0) {
		// No sample photos, directly finalize style
		void onStyleFinalized(style)
		logApplyStyle(projectStyleType, style)
		return
	}
	pushDialog(StylePreviewDialog, {
		...dialogProps,
		trackingViewName: "style_preview_dialog",
		style,
		samplePhotos: previewPhotos,
		onStyleFinalized: (finalizedStyle) => {
			void onStyleFinalized(finalizedStyle as TStyle)
			logApplyStyle(projectStyleType, finalizedStyle)
		},
	})
}

export const showStyleProcessingDialog = () => {
	replaceDialog(ConfirmDialog, {
		imageSource: styleBubblesUrl,
		imageStyle: "fit" as ImageStyle,
		title: i18n.t("store:styleState.aiStyleProcessingTitle"),
		message: i18n.t("store:styleState.aiStyleProcessingMessage"),
		primary: {
			title: i18n.t("common:action.continue"),
		},
		trackingViewName: "onboarding_improve_style",
		confirmResult: (_) => {
			resetDialogs()
		},
	})
}

export const showAddEditsToStyleDialog = () => {
	const currentStyle = store.get(currentStyleAtom)
	if (!currentStyle) {
		return
	}
	const user = store.get(userQueryAtom).data
	const isStaffPlus = isStaffPlusRole(user?.role)
	pushDialog(AddEditsToStyleDialog, {
		canCancel: true,
		style: currentStyle,
		isStaffPlus,
	})
}

const publishStyleWithErrorHandling = async (
	style: StyleInfoRecord,
	result: { name: string },
	completionHandler?: (result: {
		styleInfo: StyleInfoRecord
		references: StyleReferenceRecord[]
	}) => void,
): Promise<string | void> => {
	try {
		const newStyleResult = await publishCurrentStyleAsNewStyle(style, result.name)

		completionHandler?.(newStyleResult)

		replaceDialog(ConfirmDialog, {
			title: i18n.t("store:styleState.aiStyleCreatedTitle"),
			message: i18n.t("store:styleState.aiStyleCreatedMessage", { name: result.name }),
			trackingViewName: "generate_style_success_dialog",
			primary: {
				title: i18n.t("common:action.ok"),
				handler: () => {
					resetDialogs()
				},
			},
		})
	} catch (error) {
		console.error("Error publishing current style:", error)

		if (isDuplicateStyleNameError(error)) {
			popActiveLoadingDialogs()
			return getDuplicateStyleNameErrorMessage(result.name)
		}

		popActiveLoadingDialogs()

		if (error instanceof AxiosError && error.response?.status === 401) {
			return
		}

		if (isNetworkConnectivityError(error)) {
			showActiveNetworkErrorToast()
			return
		}
		Sentry.captureException(error)

		return "Failed to create AI Style. Please try again."
	}
}

export const showPublishCurrentStyleDialog = (
	style: StyleInfoRecord,
	dialogProps: Partial<DialogProps>,
	errorMessage?: string,
	completionHandler?: (result: {
		styleInfo: StyleInfoRecord
		references: StyleReferenceRecord[]
	}) => void,
) => {
	const initialName = style.visibility === "public" ? `Custom-${style.name}` : style.name
	pushDialog(StyleNamingDialog, {
		...dialogProps,
		trackingViewName: "public_current_style_dialog",
		title: i18n.t("store:styleState.createReferenceEditTitle"),
		description: i18n.t("store:styleState.newStyleIncludesReferences"),
		actionTitle: i18n.t("common:action.create"),
		initialName,
		errorMessage,
		onCompleteWithError: (result: { name: string }) => {
			return publishStyleWithErrorHandling(style, result, completionHandler)
		},
	})
}

export const showUpdateCurrentStyleDialog = async (
	style: StyleInfoRecord,
	dialogProps: Partial<DialogProps>,
	completionHandler?: (result: {
		styleInfo: StyleInfoRecord
		references: StyleReferenceRecord[]
	}) => void,
) => {
	let updatedStyle = style
	let references: StyleReferenceRecord[] = []
	try {
		const result = await updateCurrentStyleInTheCloud(style, dialogProps)
		updatedStyle = result?.styleInfo || style
		references = result?.references || []
	} catch (error) {
		console.error("Error updating current style:", error)
		Sentry.captureException(error)
		popActiveLoadingDialogs()
		replaceDialog(ConfirmDialog, {
			title: i18n.t("store:styleState.failedUpdateTitle"),
			message: i18n.t("store:styleState.failedUpdateMessage", { name: style.name }),
			primary: { title: i18n.t("common:action.ok") },
			trackingViewName: "update_style_failure_dialog",
		})
		return
	}

	completionHandler?.({
		styleInfo: updatedStyle,
		references: references,
	})

	replaceDialog(ConfirmDialog, {
		title: i18n.t("store:styleState.aiStyleUpdatedTitle"),
		message: i18n.t("store:styleState.aiStyleUpdatedMessage"),
		trackingViewName: "update_style_success_dialog",
		primary: {
			title: i18n.t("common:action.ok"),
			handler: () => {
				resetDialogs()
			},
		},
	})
}
